// Workflow warning dictionary (2026-10-22 spec §5.4) — the backend /workflow
// endpoints emit warning *kinds* only; every user-facing text lives here in
// the app's three-way tr() form (EN / 繁體 / 简体).
//
// Texts are ported from the interactive mockup (workflow-home-demo.html, keys
// w_gap_stmt / w_bal_break / w_inv_total / w_skip_num / w_temp_nonzero) and
// extended to the full kind set the backend writers emit:
//   - statements: continuity_flags (continuity_gap / _overlap / _date_overlap /
//     _duplicate / _balance), ocr_failed, balance_mismatch
//   - invoices:   needs_review flags prefixed invoice_ (total / direction /
//     duplicate / new_company / invoice_number / company_not_detected)
//   - gaps:       invoice_number_gap
//   - step 3:     temp_nonzero
//
// Each entry follows the mockup structure: title / What's wrong 發生了什麼 /
// Why it matters 影響 / What you can do 你可以這樣做 (the actions).
// Mockup demo data (account numbers, HCT numbers, amounts) is replaced by
// instance-neutral phrasing — the concrete numbers arrive via ctx.detail /
// ctx.targetId from the backend and are rendered by WorkflowWarningModal.

import { tr } from './i18nHelpers';

export type WorkflowWarningKind =
  | 'continuity_gap'
  | 'continuity_overlap'
  | 'continuity_date_overlap'
  | 'continuity_duplicate'
  | 'continuity_balance'
  | 'ocr_failed'
  | 'balance_mismatch'
  | 'invoice_total'
  | 'invoice_direction'
  | 'invoice_duplicate'
  | 'invoice_new_company'
  | 'invoice_number'
  | 'invoice_invoice_number'
  | 'invoice_company_not_detected'
  | 'invoice_number_gap'
  | 'temp_nonzero'
  // Review upload subpage (2026-11-03): file-level kinds for OCR-dead files
  // (targetId = the file_records id) + the generic no-flags record hold.
  | 'file_ocr_failed'
  | 'file_ocr_unclear'
  | 'file_ocr_type_mismatch'
  | 'file_ocr_encrypted'
  | 'review_pending';

export type WorkflowWarningAction =
  | { label: string; kind: 'navigate'; to: string }
  | { label: string; kind: 'gap-prompt' }
  | { label: string; kind: 'callback'; callback: () => void };

export interface WorkflowWarningDef {
  title: string;
  what: string;
  why: string;
  actions: WorkflowWarningAction[];
}

export interface WorkflowWarningCtx {
  /** Backend target_id — statement id, invoice id, or the gap number for invoice_number_gap. */
  targetId?: string;
  /** Optional instance-specific line from the backend (shown above the generic text). */
  detail?: string;
  /** Item's review-page URL for type-agnostic kinds (review_pending) — supplied
   *  by the Review upload page, which gets it from GET /upload-review. */
  reviewUrl?: string;
}

const nav = (en: string, hant: string, hans: string, to: string): WorkflowWarningAction =>
  ({ label: tr(en, hant, hans), kind: 'navigate', to });

const gapPrompt = (): WorkflowWarningAction =>
  ({ label: tr('Give a reason', '說明原因', '说明原因'), kind: 'gap-prompt' });

/** Review-route helper: '/bank-statements/review/:id' when a target id exists,
 *  otherwise the Bank Statements list page. */
const stmtReview = (ctx: WorkflowWarningCtx): string =>
  ctx.targetId ? `/bank-statements/review/${ctx.targetId}` : '/bank-statements';

/** Review-route helper: '/invoices/review/:id' when a target id exists,
 *  otherwise null (action is skipped — the backend always sends the invoice id). */
const invReview = (ctx: WorkflowWarningCtx): string | null =>
  ctx.targetId ? `/invoices/review/${ctx.targetId}` : null;

/** File Storage helper: highlight the file row when a file id exists (the
 *  Review upload page passes the file_records id as targetId for file_* kinds). */
const fileStorageHighlight = (ctx: WorkflowWarningCtx): string =>
  ctx.targetId ? `/file-storage?highlight=${ctx.targetId}` : '/file-storage';

export function workflowWarningDef(kind: WorkflowWarningKind, ctx: WorkflowWarningCtx = {}): WorkflowWarningDef {
  const invoiceReview = invReview(ctx);

  // Invoice review action + AR/AP list actions — shared tail for invoice_* kinds.
  const invoiceActions = (reviewLabel: [string, string, string]): WorkflowWarningAction[] => {
    const list: WorkflowWarningAction[] = [];
    if (invoiceReview) list.push(nav(...reviewLabel, invoiceReview));
    list.push(nav('Check the AR list', '查看應收賬', '查看应收账', '/ar'));
    list.push(nav('Check the AP list', '查看應付賬', '查看应付账', '/ap'));
    return list;
  };

  switch (kind) {
    // ── Statement continuity (mockup w_gap_stmt / w_bal_break) ──────────────
    case 'continuity_gap':
      return {
        title: tr('Continuity: missing statement', '連續性：缺少月結單', '连续性：缺少月结单'),
        what: tr(
          'Statements for this account jump from one month straight to a later month — the statement for the month in between is missing.',
          '此賬戶的月結單由一個月直接跳至之後的月份，缺少中間月份的月結單。',
          '此账户的月结单由一个月直接跳至之后的月份，缺少中间月份的月结单。',
        ),
        why: tr(
          'A missing statement means a whole month of transactions is absent and the balance chain is broken — the books cannot be reconciled.',
          '缺少月結單即缺少該月所有交易記錄，餘額鏈亦會斷裂，賬目無法核對。',
          '缺少月结单即缺少该月所有交易记录，余额链亦会断裂，账目无法核对。',
        ),
        actions: [
          nav('Upload the missing statement', '上載缺少的月結單', '上传缺少的月结单', '/workflow?step=1'),
          nav('Open Bank Statements page', '前往銀行月結單頁', '前往银行月结单页', '/bank-statements'),
        ],
      };

    case 'continuity_overlap':
      return {
        title: tr('Continuity: statements overlap', '連續性：月結單重疊', '连续性：月结单重叠'),
        what: tr(
          'Two statements for this account cover overlapping periods.',
          '此賬戶有兩張月結單的期間重疊。',
          '此账户有两张月结单的期间重叠。',
        ),
        why: tr(
          'Overlapping statements can count the same transactions twice and break the balance chain; the books cannot be reconciled until the overlap is resolved.',
          '重疊的月結單會重複計算交易並令餘額鏈斷裂；未解決重疊前，賬目無法核對。',
          '重叠的月结单会重复计算交易并令余额链断裂；未解决重叠前，账目无法核对。',
        ),
        actions: [
          nav('Open statement review to verify rows', '開啟月結單覆核核對交易', '开启月结单复核核对交易', stmtReview(ctx)),
          nav('Open Bank Statements page', '前往銀行月結單頁', '前往银行月结单页', '/bank-statements'),
        ],
      };

    case 'continuity_date_overlap':
      return {
        title: tr('Continuity: statement dates overlap', '連續性：月結單日期重疊', '连续性：月结单日期重叠'),
        what: tr(
          "Statement date ranges overlap — one statement ends after the next one begins.",
          '月結單的日期範圍重疊——上一張的結束日期晚於下一張的開始日期。',
          '月结单的日期范围重叠——上一张的结束日期晚于下一张的开始日期。',
        ),
        why: tr(
          'Overlapping date ranges can double-count transactions held on both statements and distort the balance chain.',
          '日期範圍重疊會令兩張月結單重複計算交易，並擾亂餘額鏈。',
          '日期范围重叠会令两张月结单重复计算交易，并扰乱余额链。',
        ),
        actions: [
          nav('Open statement review to verify rows', '開啟月結單覆核核對交易', '开启月结单复核核对交易', stmtReview(ctx)),
          nav('Open Bank Statements page', '前往銀行月結單頁', '前往银行月结单页', '/bank-statements'),
        ],
      };

    case 'continuity_duplicate':
      return {
        title: tr('Continuity: duplicate statement', '連續性：重複月結單', '连续性：重复月结单'),
        what: tr(
          'This statement looks like a duplicate of one already on file for the same account and period.',
          '此月結單與已存檔的同賬戶、同期間月結單重複。',
          '此月结单与已存档的同账户、同期间月结单重复。',
        ),
        why: tr(
          'A duplicate statement would import its transactions twice and break the balance chain; remove or merge the duplicate before posting.',
          '重複的月結單會將交易重複入賬並令餘額鏈斷裂；過賬前請先刪除或合併重複月結單。',
          '重复的月结单会将交易重复入账并令余额链断裂；过账前请先删除或合并重复月结单。',
        ),
        actions: [
          nav('Open statement review to verify rows', '開啟月結單覆核核對交易', '开启月结单复核核对交易', stmtReview(ctx)),
          nav('Open Bank Statements page', '前往銀行月結單頁', '前往银行月结单页', '/bank-statements'),
        ],
      };

    case 'continuity_balance':
      return {
        title: tr('Continuity: balance chain break', '連續性：餘額鏈斷裂', '连续性：余额链断裂'),
        what: tr(
          "A statement's opening balance does not match the previous statement's closing balance.",
          '月結單的期初餘額與上一張月結單的期末餘額不符。',
          '月结单的期初余额与上一张月结单的期末余额不符。',
        ),
        why: tr(
          'Likely causes: a missing statement, a missed or OCR-misread transaction, or an unrecorded bank fee.',
          '可能原因：缺少月結單、交易漏讀／OCR 誤讀，或銀行收費未入賬。',
          '可能原因：缺少月结单、交易漏读／OCR 误读，或银行收费未入账。',
        ),
        actions: [
          nav('Open statement review to verify rows', '開啟月結單覆核核對交易', '开启月结单复核核对交易', stmtReview(ctx)),
          nav('Check for missing statements', '檢查缺少的月結單', '检查缺少的月结单', '/workflow?step=1'),
          nav('Open Bank Statements page', '前往銀行月結單頁', '前往银行月结单页', '/bank-statements'),
        ],
      };

    // ── Statement OCR / totals ──────────────────────────────────────────────
    case 'ocr_failed':
      return {
        title: tr('OCR failed on this statement', 'OCR 無法讀取此月結單', 'OCR 无法读取此月结单'),
        what: tr(
          'The OCR engine could not read this statement, so its transactions were not extracted automatically.',
          'OCR 引擎無法讀取此月結單，未能自動提取其交易。',
          'OCR 引擎无法读取此月结单，未能自动提取其交易。',
        ),
        why: tr(
          'Until the statement is entered manually or re-uploaded as a clearer scan, its month cannot be reconciled.',
          '未完成 OCR 前，需要人手輸入月結單或重新上載較清晰的掃描，該月才能核對。',
          '未完成 OCR 前，需要人手输入月结单或重新上载较清晰的扫描，该月才能核对。',
        ),
        actions: [
          nav('Open statement review to enter rows manually', '開啟月結單覆核人手輸入', '开启月结单复核人手输入', stmtReview(ctx)),
          nav('Re-upload a clearer scan', '重新上載較清晰的掃描', '重新上传较清晰的扫描', '/workflow?step=1'),
        ],
      };

    case 'balance_mismatch':
      return {
        title: tr('Statement totals do not reconcile', '月結單金額不符', '月结单金额不符'),
        what: tr(
          "The imported transaction amounts do not reconcile with the statement's stated totals.",
          '匯入的交易金額與月結單列明的合計不符。',
          '导入的交易金额与月结单列明的合计不符。',
        ),
        why: tr(
          'A missed or misread line breaks the bank reconciliation; verify the rows in review before posting.',
          '漏讀或誤讀的交易會令銀行核對失真；過賬前請先於覆核核對每行。',
          '漏读或误读的交易会令银行核对失真；过账前请先于复核核对每行。',
        ),
        actions: [
          nav('Open statement review to verify rows', '開啟月結單覆核核對交易', '开启月结单复核核对交易', stmtReview(ctx)),
          nav('Open Bank Statements page', '前往銀行月結單頁', '前往银行月结单页', '/bank-statements'),
        ],
      };

    // ── Invoice review flags (mockup w_inv_total) ───────────────────────────
    case 'invoice_total':
      return {
        title: tr('OCR: total mismatch', 'OCR：總額不符', 'OCR：总额不符'),
        what: tr(
          "The invoice's printed total and the OCR-read total disagree.",
          '此發票的印刷總額與 OCR 讀取總額不符。',
          '此发票的印刷总额与 OCR 读取总额不符。',
        ),
        why: tr(
          'With blurry scans or handwritten-vs-printed disagreement, the system keeps the more credible signal and flags the invoice for review.',
          '掃描模糊或手寫金額與印刷金額不一致時，系統取較可信者並標記覆核。',
          '扫描模糊或手写金额与印刷金额不一致时，系统取较可信者并标记复核。',
        ),
        actions: [
          ...(invoiceReview
            ? [nav('Open this invoice in review to verify', '開啟此發票覆核核對', '开启此发票复核核对', invoiceReview)]
            : []),
          nav('Re-upload a clearer scan', '重新上載較清晰的掃描', '重新上传较清晰的扫描', '/workflow?step=2'),
        ],
      };

    case 'invoice_direction':
      return {
        title: tr('Invoice direction needs review', '發票方向待覆核', '发票方向待复核'),
        what: tr(
          'OCR could not confidently tell whether this is a sales (outgoing) or purchase (incoming) invoice.',
          'OCR 未能可靠判斷此發票是銷售（流出）還是採購（流入）發票。',
          'OCR 未能可靠判断此发票是销售（流出）还是采购（流入）发票。',
        ),
        why: tr(
          'Direction decides the posting side (AR vs AP); a wrong guess would post the invoice to the wrong ledger.',
          '方向決定過賬方向（應收賬還是應付賬）；猜錯會把發票過賬至錯誤的賬項。',
          '方向决定过账方向（应收账还是应付账）；猜错会把发票过账至错误的账项。',
        ),
        actions: invoiceActions(['Open this invoice in review to set the direction', '開啟此發票覆核設定方向', '开启此发票复核设定方向']),
      };

    case 'invoice_duplicate':
      return {
        title: tr('Possible duplicate invoice', '疑似重複發票', '疑似重复发票'),
        what: tr(
          'This invoice looks like a duplicate of one already on file.',
          '此發票疑似與已存檔的發票重複。',
          '此发票疑似与已存档的发票重复。',
        ),
        why: tr(
          'A duplicate import would double-count revenue or expenses and inflate AR/AP; confirm or discard it in review.',
          '重複匯入會重複計算收入或費用並虛增應收／應付賬；請於覆核確認或棄用。',
          '重复导入会重复计算收入或费用并虚增应收／应付账；请于复核确认或弃用。',
        ),
        actions: invoiceActions(['Open this invoice in review to confirm or discard', '開啟此發票覆核確認或棄用', '开启此发票复核确认或弃用']),
      };

    case 'invoice_new_company':
      return {
        title: tr('New counterparty detected', '偵測到新對家', '检测到新对家'),
        what: tr(
          'The counterparty on this invoice has not been seen before.',
          '此發票的對家首次出現。',
          '此发票的对家首次出现。',
        ),
        why: tr(
          'New counterparties need a customer/supplier record so AR/AP aging and later matching work correctly.',
          '新對家需要建立客戶／供應商記錄，應收／應付賬齡及之後的配對才能正確運作。',
          '新对家需要建立客户／供应商记录，应收／应付账龄及之后的配对才能正确运作。',
        ),
        actions: invoiceActions(['Open this invoice in review to confirm the counterparty', '開啟此發票覆核確認對家', '开启此发票复核确认对家']),
      };

    // 'invoice_number' and 'invoice_invoice_number' are the same printed-number
    // doubt — the backend emits invoice_ + 'invoice_number' (file-storage.ts
    // pushes flag 'invoice_number', workflow.ts prefixes 'invoice_'), so
    // 'invoice_invoice_number' is the kind that reaches the UI. 'invoice_number'
    // stays in the union as the unprefixed twin and resolves to the same entry.
    case 'invoice_number':
    case 'invoice_invoice_number':
      return {
        title: tr('OCR: invoice number doubt', 'OCR：發票編號存疑', 'OCR：发票编号存疑'),
        what: tr(
          'The printed invoice number could not be read confidently and may be wrong.',
          '印刷發票編號未能可靠讀取，可能有誤。',
          '印刷发票编号未能可靠读取，可能有误。',
        ),
        why: tr(
          'The invoice number drives the number-continuity check; a misread number shows up as a false gap in the chain.',
          '發票編號用於編號連續性檢查；讀錯編號會在鏈上顯示為假的跳號。',
          '发票编号用于编号连续性检查；读错编号会在链上显示为假的跳号。',
        ),
        actions: invoiceActions(['Open this invoice in review to verify the number', '開啟此發票覆核核對編號', '开启此发票复核核对编号']),
      };

    case 'invoice_company_not_detected':
      return {
        title: tr('Counterparty not detected', '未能偵測對家', '未能检测到对家'),
        what: tr(
          'The counterparty (company) on this invoice could not be detected from the scan.',
          '未能從掃描中偵測此發票的對家（公司）。',
          '未能从扫描中检测此发票的对家（公司）。',
        ),
        why: tr(
          'Without a counterparty the invoice cannot be matched to a customer/supplier or posted to AR/AP; fill it in during review.',
          '沒有對家，發票無法配對客戶／供應商或過賬至應收／應付賬；請於覆核補上。',
          '没有对家，发票无法配对客户／供应商或过账至应收／应付账；请于复核补上。',
        ),
        actions: invoiceActions(['Open this invoice in review to fill in the counterparty', '開啟此發票覆核補上對家', '开启此发票复核补上对家']),
      };

    // ── Invoice number gaps (mockup w_skip_num) ─────────────────────────────
    case 'invoice_number_gap': {
      const n = ctx.targetId;
      return {
        title: tr('Skipped invoice number', '發票跳號', '发票跳号'),
        what: n
          ? tr(
              `Invoice ${n} is missing from the number sequence.`,
              `發票編號 ${n} 於編號序列中缺失。`,
              `发票编号 ${n} 于编号序列中缺失。`,
            )
          : tr(
              'An invoice number is missing from the sequence.',
              '編號序列中缺少一個發票編號。',
              '编号序列中缺少一个发票编号。',
            ),
        why: tr(
          'Gaps usually mean voided or cancelled invoices, or a missing upload — auditors expect an explanation.',
          '跳號可能代表作廢、取消或漏上載；審計時需說明原因。',
          '跳号可能代表作废、取消或漏上传；审计时需说明原因。',
        ),
        actions: [
          gapPrompt(),
          nav('Upload the missing invoice if it exists', '如發票存在請上載', '如发票存在请上传', '/workflow?step=2'),
          nav('Check the AR list', '查看應收賬', '查看应收账', '/ar'),
        ],
      };
    }

    // ── Step 3 parked money (mockup w_temp_nonzero) ─────────────────────────
    case 'temp_nonzero':
      return {
        title: tr('Temp accounts are non-zero', '暫記科目尚有餘額', '暂记科目尚有余额'),
        what: tr(
          'The temporary receipt/payment accounts still hold balances that have not been cleared.',
          '暫收款／暫付款科目尚有未清理的餘額。',
          '暂收款／暂付款科目尚有未清理的余额。',
        ),
        why: tr(
          'Temp accounts should be cleared to zero before closing the period; parked money distorts AR/AP.',
          '暫記科目應於結賬前歸零；未配對款項停留在暫記會令應收／應付賬失真。',
          '暂记科目应于结账前归零；未配对款项停留在暂记会令应收／应付账失真。',
        ),
        actions: [
          nav('Back to Step 3 to finish matching', '返回步驟 3 完成配對', '返回步骤 3 完成配对', '/workflow?step=3'),
          nav('See the Temp lists on the Summary', '查看總覽的暫記清單', '查看总览的暂记清单', '/workflow/summary'),
        ],
      };

    // ── Review upload: OCR-dead files (targetId = file_records id) ──────────
    case 'file_ocr_failed':
      return {
        title: tr('OCR could not read this file', 'OCR 無法讀取此文件', 'OCR 无法读取此文件'),
        what: tr(
          'The OCR engine failed on this file, so no statement or invoice was created from it.',
          'OCR 引擎無法讀取此文件，未能產生任何月結單或發票。',
          'OCR 引擎无法读取此文件，未能产生任何月结单或发票。',
        ),
        why: tr(
          'Until it is re-uploaded as a clearer scan or entered manually, this document is absent from the books.',
          '在重新上載較清晰的掃描或人手輸入之前，此文件不會入賬。',
          '在重新上传较清晰的扫描或手动输入之前，此文件不会入账。',
        ),
        actions: [
          nav('Open this file in File Storage', '於文件儲存開啟此文件', '于文件存储开启此文件', fileStorageHighlight(ctx)),
          nav('Re-upload a clearer scan', '重新上載較清晰的掃描', '重新上传较清晰的扫描', '/file-upload'),
        ],
      };

    case 'file_ocr_unclear':
      return {
        title: tr('OCR result unclear', 'OCR 結果不明', 'OCR 结果不明'),
        what: tr(
          'The scan was too unclear for OCR to extract usable data, so no statement or invoice was created.',
          '掃描太模糊，OCR 未能提取可用資料，未產生月結單或發票。',
          '扫描太模糊，OCR 未能提取可用资料，未产生月结单或发票。',
        ),
        why: tr(
          'A clearer scan is needed before this document can enter the books.',
          '需要較清晰的掃描，此文件才能入賬。',
          '需要较清晰的扫描，此文件才能入账。',
        ),
        actions: [
          nav('Open this file in File Storage', '於文件儲存開啟此文件', '于文件存储开启此文件', fileStorageHighlight(ctx)),
          nav('Re-upload a clearer scan', '重新上載較清晰的掃描', '重新上传较清晰的扫描', '/file-upload'),
        ],
      };

    case 'file_ocr_type_mismatch':
      return {
        title: tr('Document type mismatch', '文件類型不符', '文件类型不符'),
        what: tr(
          'The document type detected from the content disagreed with the selected upload type, so the file was stored without processing.',
          '從內容偵測的文件類型與所選上載類型不符，系統只作儲存而未處理。',
          '从内容检测的文件类型与所选上传类型不符，系统只作存储而未处理。',
        ),
        why: tr(
          'Processing a document as the wrong type would import it into the wrong books.',
          '以錯誤類型處理文件會將其匯入錯誤的賬目。',
          '以错误类型处理文件会将其导入错误的账目。',
        ),
        actions: [
          nav('Open this file in File Storage', '於文件儲存開啟此文件', '于文件存储开启此文件', fileStorageHighlight(ctx)),
          nav('Re-upload with the correct type', '以正確類型重新上載', '以正确类型重新上传', '/file-upload'),
        ],
      };

    case 'file_ocr_encrypted':
      return {
        title: tr('Password-protected PDF', '受密碼保護的 PDF', '受密码保护的 PDF'),
        what: tr(
          'This PDF is encrypted and needs its password before OCR can read it.',
          '此 PDF 已加密，需先輸入密碼才能進行 OCR。',
          '此 PDF 已加密，需先输入密码才能进行 OCR。',
        ),
        why: tr(
          'The file is stored safely, but its contents stay out of the books until it is unlocked.',
          '文件已安全儲存，但解鎖前其內容不會入賬。',
          '文件已安全存储，但解锁前其内容不会入账。',
        ),
        actions: [
          nav('Open this file in File Storage to enter the password', '於文件儲存開啟此文件並輸入密碼', '于文件存储开启此文件并输入密码', fileStorageHighlight(ctx)),
        ],
      };

    // ── Review upload: record held at needs_review with no specific flag ────
    case 'review_pending':
      return {
        title: tr('Waiting for your confirmation', '待你確認', '待你确认'),
        what: tr(
          'This document was imported and is held for review — its figures do not count in the books until confirmed.',
          '此文件已匯入並等待覆核——確認前其數字不會計入賬目。',
          '此文件已导入并等待复核——确认前其数字不会计入账目。',
        ),
        why: tr(
          'Review is the safety check that catches OCR misreads before they reach the financial statements.',
          '覆核是在 OCR 誤讀進入財務報表前把關的最後檢查。',
          '复核是在 OCR 误读进入财务报表前把关的最后检查。',
        ),
        actions: [
          nav('Open the review page', '開啟覆核頁面', '开启复核页面', ctx.reviewUrl || '/file-upload/review'),
        ],
      };
  }
}
